import React, { useCallback, useState } from 'react';
import moment from 'moment';

import { IBuilderSaveResult } from 'components/EmbeddedBuilder';
import { useTypedSelector } from 'hooks';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { DEFAULT_TIMEZONE } from '../configStudio/configModel';
import { ConfigHandoff, ConfigIntent, ConfigSubmit } from '../configStudio/configSubmission';
import { ConfigReviewPanel, TC } from '../configStudio/kit/ConfigPanel';
import ConfigResultView from '../configStudio/kit/ConfigResultView';
import ConfigWizardRoot from '../configStudio/kit/ConfigWizardRoot';
import { RuleOutcomeDetailsStep, RuleOutcomeTypeStep } from './RuleOutcomeSteps';
import RuleScheduleStep from './RuleScheduleStep';
import { RuleTriggerDetailsStep, RuleTriggerTypeStep } from './RuleTriggerSteps';
import { buildRuleValues, TransactionRuleValues } from './ruleAdapter';
import {
  initialRuleState,
  nextRuleStep,
  previousRuleStep,
  RULE_STEP_NUMBER,
  RuleStep,
  ruleStepAt,
  RuleWizardState
} from './ruleModel';
import { ruleReviewRows } from './ruleReviewRows';

const RV = `${TC}.rule.review`;

const STEPS: Record<
  Exclude<RuleStep, 'review'>,
  React.FC<React.ComponentProps<typeof RuleScheduleStep>>
> = {
  trigger: RuleTriggerTypeStep,
  triggerDetails: RuleTriggerDetailsStep,
  outcome: RuleOutcomeTypeStep,
  outcomeDetails: RuleOutcomeDetailsStep,
  schedule: RuleScheduleStep
};

interface IRuleWizardProps {
  onSubmit: ConfigSubmit<TransactionRuleValues>;
  /** Back out of the flow from its first step. */
  onExit: () => void;
  /** Told about every record a save created. */
  onHandoff: ConfigHandoff;
  /** Resume at a given state (tests, or a prefill). */
  initialState?: RuleWizardState;
}

// 54 / 62 draw the start already filled with today; the user may move it.
const startingNow = (state: RuleWizardState): RuleWizardState => ({
  ...state,
  beginsAt: moment().startOf('minute').format()
});

interface ISaved {
  name: string;
  result: IBuilderSaveResult;
}

/** The standalone "Let's setup your Rule" flow (46 → 47 → 45 → 48-53 → 54 → 55). */
const RuleWizard: React.FC<IRuleWizardProps> = ({ onSubmit, onExit, onHandoff, initialState }) => {
  const { t } = useMultiLanguageTranslation();
  const timezone = useTypedSelector(
    store => store.global?.tenantConfig?.localization?.timezone ?? DEFAULT_TIMEZONE
  );
  const [state, setState] = useState<RuleWizardState>(
    () => initialState ?? startingNow(initialRuleState())
  );
  const [saved, setSaved] = useState<ISaved | null>(null);
  const update = useCallback(
    (change: (current: RuleWizardState) => RuleWizardState) => setState(change),
    []
  );
  const next = useCallback(
    () => setState(current => ({ ...current, step: nextRuleStep(current.step) })),
    []
  );
  const back = () =>
    state.step === 'trigger'
      ? onExit()
      : setState(current => ({ ...current, step: previousRuleStep(current.step) }));
  const jump = useCallback(
    (number: number) => setState(current => ({ ...current, step: ruleStepAt(number) })),
    []
  );

  const submit = async (intent: ConfigIntent) => {
    const now = moment().toISOString();
    const result = await onSubmit({
      intent,
      values: buildRuleValues(state, { t, timezone, now: () => now })
    });
    if (result !== null) {
      setSaved({ name: state.name.trim(), result });
      setState(startingNow(initialRuleState()));
      onHandoff(result);
    }
  };

  const renderStep = () => {
    if (saved !== null) {
      return (
        <ConfigResultView
          entity={t(`${TC}.rule.entity`)}
          name={saved.name}
          result={saved.result}
          onCreateAnother={() => setSaved(null)}
        />
      );
    }
    if (state.step === 'review') {
      return (
        <ConfigReviewPanel<RuleStep>
          title={t(`${RV}.ready`)}
          question={t(`${RV}.question`)}
          onBack={back}
          nameLabel={t(`${RV}.name`)}
          namePlaceholder={t(`${RV}.namePlaceholder`)}
          nameRequired={t(`${RV}.nameRequired`)}
          name={state.name}
          onName={name => update(current => ({ ...current, name }))}
          rows={ruleReviewRows(state, t, timezone, moment().format())}
          onEdit={step => update(current => ({ ...current, step }))}
          confirmLabel={t(`${TC}.finalise`)}
          onSubmit={submit}
          testId="orbit-rule-step-review"
        />
      );
    }
    const Step = STEPS[state.step];
    return <Step state={state} update={update} next={next} back={back} />;
  };

  return (
    <ConfigWizardRoot
      testId="orbit-rule-wizard"
      step={state.step}
      onJump={saved === null && RULE_STEP_NUMBER[state.step] !== null ? jump : null}
    >
      {renderStep()}
    </ConfigWizardRoot>
  );
};

export default RuleWizard;
